// src/pages/Dashboard.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";

const PRIMARY = "#1F2937";

function StatCard({ label, value, sub, color, icon }) {
  return (
    <div style={{ background:"#fff", border:"1px solid var(--gray-200)", borderRadius:12, padding:"20px 22px",
      display:"flex", alignItems:"flex-start", gap:14, boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
      <div style={{ width:44, height:44, borderRadius:10, background:`${color}18`, display:"flex",
        alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>
        {icon}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:26, fontWeight:800, color:PRIMARY, lineHeight:1.1 }}>{value}</div>
        <div style={{ fontSize:13, fontWeight:600, color:"var(--gray-700)", marginTop:2 }}>{label}</div>
        {sub && <div style={{ fontSize:12, color:"var(--gray-400)", marginTop:2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function ProgressBar({ pct, color="#10B981", height=8 }) {
  return (
    <div style={{ height, background:"var(--gray-100)", borderRadius:99, overflow:"hidden", width:"100%" }}>
      <div style={{ height:"100%", width:`${Math.min(pct,100)}%`, background:color,
        borderRadius:99, transition:"width 0.6s ease" }}/>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize:13, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em",
      color:"var(--gray-400)", marginBottom:14, marginTop:6 }}>{children}</div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    fetch("/api/dashboard")
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setData(d); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:80 }}>
      <span className="spinner" style={{ width:32, height:32 }}/>
    </div>
  );

  if (error) return (
    <div className="card" style={{ padding:32, textAlign:"center", color:"var(--red)" }}>⚠ {error}</div>
  );

  const { summary, courseStats, recentCompletions, categoryBreakdown, scopeNote } = data;
  const isSupervisor = user?.role === "supervisor";

  function pctColor(pct) {
    if (pct >= 80) return "#10B981";
    if (pct >= 50) return "#F59E0B";
    return "#EF4444";
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">
            {scopeNote || "Platform-wide training overview"}
          </div>
        </div>
      </div>

      {scopeNote && (
        <div style={{ fontSize:13, color:"#92400E", background:"#FFF7ED", border:"1px solid #FED7AA",
          borderRadius:8, padding:"8px 14px", marginBottom:20 }}>
          ⚠ {scopeNote}
        </div>
      )}

      {/* ── Key stats row ──────────────────────────────────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:14, marginBottom:28 }}>
        <StatCard icon="📚" label="Live Courses"    value={summary.liveCourses}    color="#6366F1"
          sub={summary.draftCourses ? `${summary.draftCourses} draft${summary.testCourses?`, ${summary.testCourses} test`:""}` : undefined}/>
        <StatCard icon="👥" label="Active Learners"  value={summary.activeUsers} color="#3B82F6" />
        <StatCard icon="✅" label="Completions"       value={summary.totalCompleted}  color="#10B981"
          sub={`${summary.totalInProgress} in progress`}/>
        <StatCard icon="📊" label="Overall Completion" value={`${summary.overallPct}%`} color={pctColor(summary.overallPct)}
          sub={`across ${summary.totalEnrolments} enrolments`}/>
        {summary.mandatoryCount > 0 && (
          <StatCard icon="🎯" label="Fully Compliant" value={`${summary.compliancePct}%`} color={pctColor(summary.compliancePct)}
            sub={`${summary.compliantUsers}/${summary.activeUsers} learners · ${summary.mandatoryCount} mandatory course${summary.mandatoryCount!==1?"s":""}`}/>
        )}
        {summary.policyCount > 0 && (
          <StatCard icon="📋" label="Policy Acceptance" value={`${summary.policyCompliancePct}%`} color={pctColor(summary.policyCompliancePct)}
            sub={`${summary.pendingPolicyAcceptances} pending · ${summary.policyCount} polic${summary.policyCount!==1?"ies":"y"}`}/>
        )}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, alignItems:"start" }}>

        {/* ── Course completion breakdown ────────────────────────────────── */}
        <div className="card" style={{ padding:"20px 24px" }}>
          <SectionTitle>Course Completion</SectionTitle>
          {courseStats.length === 0 ? (
            <div style={{ fontSize:13, color:"var(--gray-400)", textAlign:"center", padding:"24px 0" }}>
              No live courses yet.
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              {courseStats.map(c => (
                <div key={c.id}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:5 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <span style={{ fontSize:13, fontWeight:600, color:"var(--gray-800)" }}>{c.name}</span>
                      {c.mandatory && (
                        <span style={{ marginLeft:7, fontSize:10, fontWeight:700, color:"#DC2626",
                          background:"#FEE2E2", padding:"1px 6px", borderRadius:99 }}>MANDATORY</span>
                      )}
                    </div>
                    <div style={{ fontSize:13, fontWeight:700, color:pctColor(c.pct), marginLeft:12, flexShrink:0 }}>
                      {c.pct}%
                    </div>
                  </div>
                  <ProgressBar pct={c.pct} color={pctColor(c.pct)} />
                  <div style={{ fontSize:11, color:"var(--gray-400)", marginTop:3 }}>
                    {c.completed}/{c.enrolled} completed
                    {c.inProgress > 0 && ` · ${c.inProgress} in progress`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

          {/* ── Category breakdown (admin: all, supervisor: just theirs) ── */}
          {categoryBreakdown.length > 0 && (
            <div className="card" style={{ padding:"20px 24px" }}>
              <SectionTitle>By Category</SectionTitle>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {categoryBreakdown.map(cat => (
                  <div key={cat.id} style={{ display:"grid", gridTemplateColumns:"1fr 80px", gap:10, alignItems:"center" }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:"var(--gray-800)", marginBottom:4 }}>
                        {cat.name}
                        <span style={{ fontWeight:400, color:"var(--gray-400)", marginLeft:6, fontSize:12 }}>
                          {cat.courseCount} course{cat.courseCount!==1?"s":""}
                        </span>
                      </div>
                      <ProgressBar pct={cat.avgPct} color={pctColor(cat.avgPct)} height={6} />
                    </div>
                    <div style={{ fontSize:15, fontWeight:700, color:pctColor(cat.avgPct), textAlign:"right" }}>
                      {cat.avgPct}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Recent completions ─────────────────────────────────────── */}
          <div className="card" style={{ padding:"20px 24px" }}>
            <SectionTitle>Recent Completions (Last 30 Days)</SectionTitle>
            {recentCompletions.length === 0 ? (
              <div style={{ fontSize:13, color:"var(--gray-400)", textAlign:"center", padding:"20px 0" }}>
                No completions in the last 30 days.
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                {recentCompletions.map((r, i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0",
                    borderBottom: i < recentCompletions.length - 1 ? "1px solid var(--gray-100)" : "none" }}>
                    <div style={{ width:32, height:32, borderRadius:"50%", background:"#ECFDF5",
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>
                      ✅
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:"var(--gray-800)" }}>{r.userName}</div>
                      <div style={{ fontSize:12, color:"var(--gray-500)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                        {r.courseName}
                      </div>
                    </div>
                    <div style={{ fontSize:11, color:"var(--gray-400)", flexShrink:0 }}>
                      {new Date(r.completedAt).toLocaleDateString("en-GB")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Compliance summary ─────────────────────────────────────── */}
          {summary.mandatoryCount > 0 && (
            <div className="card" style={{ padding:"20px 24px",
              background: summary.compliancePct >= 80 ? "#F0FDF4" : summary.compliancePct >= 50 ? "#FFFBEB" : "#FEF2F2",
              border: `1px solid ${summary.compliancePct >= 80 ? "#86EFAC" : summary.compliancePct >= 50 ? "#FDE68A" : "#FECACA"}` }}>
              <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ fontSize:36 }}>
                  {summary.compliancePct >= 80 ? "🟢" : summary.compliancePct >= 50 ? "🟡" : "🔴"}
                </div>
                <div>
                  <div style={{ fontSize:18, fontWeight:800, color:pctColor(summary.compliancePct) }}>
                    {summary.compliancePct}% Compliant
                  </div>
                  <div style={{ fontSize:13, color:"var(--gray-600)", marginTop:2 }}>
                    {summary.compliantUsers} of {summary.activeUsers} learners have completed all {summary.mandatoryCount} mandatory course{summary.mandatoryCount!==1?"s":""}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
